/**
 * Custom Product Bundle JavaScript
 * Handles dynamic pricing, image switching, and cart functionality
 */

class CustomProductBundle {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    
    // Load configuration from JSON
    this.config = JSON.parse(
      document.getElementById(`product-config-${this.sectionId}`).textContent
    );
    
    // Initialize state
    this.state = {
      packSize: null,
      weightPlates: null,
      insert: null,
      quantity: 1,
      isBaseProductView: true // Track if showing base product or selected combination
    };
    
    // Cache DOM elements
    this.elements = {
      packSizeBtns: container.querySelectorAll('[data-option="pack-size"]'),
      weightPlatesBtns: container.querySelectorAll('[data-option="weight-plates"]'),
      insertBtns: container.querySelectorAll('[data-option="insert"]'),
      qtyInput: container.querySelector('.qty-input'),
      qtyMinus: container.querySelector('.qty-btn.minus'),
      qtyPlus: container.querySelector('.qty-btn.plus'),
      priceDisplay: container.querySelector('#product-price .price-amount'),
      comparePriceDisplay: container.querySelector('#product-price .compare-price-amount'),
      savePriceDisplay: container.querySelector('#product-price .save-price-amount'),      
      priceBreakdown: container.querySelector('#price-breakdown'),
      mainImage: container.querySelector('#main-product-image'),
      thumbItems: container.querySelectorAll('.thumb-item'),
      addToCartBtn: container.querySelector('#add-to-cart-btn'),
      morePaymentBtn: container.querySelector('#more-payment-option'),
      loadingSpinner: container.querySelector('.loading-spinner')
    };
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.initializeGallery(); // Initialize with base product images
    this.setDefaultSelections();
    this.initThumbnailSlider(); 
    this.updateDisplay();
    this.initSwipeGesture();
  }


  // Initialize Mobile Gallery Swiper
  initSwipeGesture() {
    const isMobile = () => window.innerWidth < 740;

    const mainImageContainer = this.elements.mainImage.parentElement;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    const SWIPE_THRESHOLD = 50;
    const VERTICAL_LOCK = 75;

    mainImageContainer.addEventListener('touchstart', (e) => {
      if (!isMobile()) return; // ← Guard

      touchStartX = e.changedTouches[0].clientX;
      touchStartY = e.changedTouches[0].clientY;
    }, { passive: true });

    mainImageContainer.addEventListener('touchend', (e) => {
      if (!isMobile()) return; // ← Guard

      touchEndX = e.changedTouches[0].clientX;
      touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX;
      const deltaY = Math.abs(touchEndY - touchStartY);

      if (deltaY > VERTICAL_LOCK) return;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

      if (deltaX < 0) {
        this.navigateImage('next');
      } else {
        this.navigateImage('prev');
      }
    }, { passive: true });
  }


  navigateImage(direction) {
    // Get only currently visible/relevant thumbs
    const visibleThumbs = [...this.elements.thumbItems].filter(thumb => {
      // If it's a combination thumb, only include if it's currently shown
      if (thumb.dataset.imageType === 'combination') {
        return !thumb.closest('.hidden') && thumb.offsetParent !== null;
      }
      return true;
    });

    const activeIndex = visibleThumbs.findIndex(thumb =>
      thumb.classList.contains('active')
    );

    let nextIndex;

    if (direction === 'next') {
      nextIndex = activeIndex < visibleThumbs.length - 1
        ? activeIndex + 1
        : 0; // Loop back to first
    } else {
      nextIndex = activeIndex > 0
        ? activeIndex - 1
        : visibleThumbs.length - 1; // Loop to last
    }

    const targetThumb = visibleThumbs[nextIndex];
    if (targetThumb) {
      this.handleThumbClick(targetThumb);
      this.scrollThumbnailIntoView(targetThumb); // Keep thumb strip in sync
    }
  }

  scrollThumbnailIntoView(thumb) {
    // Smoothly scroll the thumbnail strip so active thumb is visible
    thumb.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }


  /**
   * Initialize thumbnail slider navigation
   */
  initThumbnailSlider() {
    const sliderComponent = this.container.querySelector('slider-component');
    
    if (sliderComponent) {
      const prevBtn = sliderComponent.querySelector('.slider-button--prev');
      const nextBtn = sliderComponent.querySelector('.slider-button--next');
      const sliderContainer = sliderComponent.querySelector('.slider');

      if (prevBtn && nextBtn && sliderContainer) {
        // Get the step value from button (default to 1 if not set)
        const step = parseInt(prevBtn.dataset.step) || 3;
        
        // Calculate scroll distance based on thumbnail width
        // Assuming each thumbnail + padding is around 120px
        const scrollDistance = step * 120;

        prevBtn.addEventListener('click', () => {
          sliderContainer.scrollLeft -= scrollDistance;
          this.updateSliderButtonStates(sliderContainer, prevBtn, nextBtn);
        });

        nextBtn.addEventListener('click', () => {
          sliderContainer.scrollLeft += scrollDistance;
          this.updateSliderButtonStates(sliderContainer, prevBtn, nextBtn);
        });

        // Initial button state
        this.updateSliderButtonStates(sliderContainer, prevBtn, nextBtn);

        // Update button states on scroll
        sliderContainer.addEventListener('scroll', () => {
          this.updateSliderButtonStates(sliderContainer, prevBtn, nextBtn);
        });
      }
    }
  }

  /**
   * Update slider button disabled states based on scroll position
   */
  updateSliderButtonStates(container, prevBtn, nextBtn) {
    // Check if at the start
    const isAtStart = container.scrollLeft <= 0;
    
    // Check if at the end (with 5px tolerance for rounding)
    const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;

    // Disable/enable buttons
    prevBtn.disabled = isAtStart;
    nextBtn.disabled = isAtEnd;

    // Optional: Add visual feedback classes
    prevBtn.classList.toggle('is-disabled', isAtStart);
    nextBtn.classList.toggle('is-disabled', isAtEnd);
  }


  /**   
  * Initialize gallery with base product images on page load   
  */  
  initializeGallery() {    
    if (this.config.baseProductImages && this.config.baseProductImages.length > 0) {      
      // Set first base product image as main      
      // this.elements.mainImage.src = this.config.baseProductImages[0].src;      
      // this.elements.mainImage.alt = this.config.baseProductImages[0].alt;    

      // Populate thumbnails with base product images      
      this.updateThumbnailsGallery(this.config.baseProductImages, 'base'); 

      this.state.isBaseProductView = true;    
    }  
  }  
  
  /**   
  * Update thumbnails gallery with images   
  */  
  updateThumbnailsGallery(images, type = 'base') {    
    if (!this.elements.thumbnailsContainer) return;    
    // Clear existing thumbnails    
    this.elements.thumbnailsContainer.innerHTML = '';    
    // Create thumbnail elements    
    images.forEach((image, index) => {      
      const div = document.createElement('div');      
      div.className = `thumb-item thumbnail-list__item slider__slide ${index === 0 ? 'active' : ''}`;      
      div.dataset.imageSrc = image.src;      
      div.dataset.imageAlt = image.alt;      
      div.dataset.imageType = type;            

      const img = document.createElement('img');      
      img.width = 'auto';      
      img.height = 'auto';      
      img.src = image.src;      
      img.alt = image.alt;      
      img.loading = 'lazy';            

      div.appendChild(img);      
      this.elements.thumbnailsContainer.appendChild(div);    
    });    
    // Re-cache thumb items since DOM changed    
    this.elements.thumbItems = this.container.querySelectorAll('.thumb-item');  

    // Re-bind thumbnail click events    
    this.bindThumbnailEvents(); 
    this.initThumbnailSlider(); // Reinitialize
  }  
  
  /**
   * Set default selections (first option in each group)
   */
  // setDefaultSelections() {
  //   // Select first pack size
  //   if (this.elements.packSizeBtns.length > 0) {
  //     this.state.packSize = this.elements.packSizeBtns[0].dataset.value;      
  //     this.elements.packSizeBtns[0].classList.add('active');
  //   }
    
  //   // Select first weight plates option
  //   if (this.elements.weightPlatesBtns.length > 0) {
  //     this.state.weightPlates = this.elements.weightPlatesBtns[0].dataset.value;      
  //     this.elements.weightPlatesBtns[0].classList.add('active');
  //   }
    
  //   // Select first insert option
  //   if (this.elements.insertBtns.length > 0) {
  //     this.state.insert = this.elements.insertBtns[0].dataset.value;      
  //     this.elements.insertBtns[0].classList.add('active');
  //   }
  // }

/**
 * Set default selections based on URL parameters,
 * falling back to the first option in each group.
 *
 * Supported params: ?pack=single|pair&plates=yes|no&insert=yes|no
 */
  setDefaultSelections() {
    // 🧪 TESTING: Hardcoded params — replace with real URL when done  
    // const params = new URLSearchParams('pack=single&plates=no&insert=no');

    // ❌ PRODUCTION: Comment out above and uncomment this:      
     const params = new URLSearchParams(window.location.search);

    /**
     * Activates the button matching the given URL param value.
     * Falls back to index [0] if param is absent or no match found.
     *
     * @param {NodeList|HTMLElement[]} btns   - The button group
     * @param {string}                 param  - URL param key (e.g. "pack")
     * @param {string}                 stateKey - Key to update on this.state
     */
    const applySelection = (btns, param, stateKey) => {
      if (btns.length === 0) return;

      const urlValue = params.get(param); // e.g. "single", "yes", "no"

      // Try to find a button whose data-value matches the URL param
      let matchIndex = urlValue
        ? [...btns].findIndex(btn => btn.dataset.value === urlValue)
        : -1;

      // No match found (param absent, typo, invalid value) → default to [0]
      if (matchIndex === -1) matchIndex = 0;

      this.state[stateKey] = btns[matchIndex].dataset.value;
      btns[matchIndex].classList.add('active');
    };

    // pack:   single → [0]  |  pair   → [1]
    applySelection(this.elements.packSizeBtns,    'pack',   'packSize');

    // plates: yes    → [0]  |  no     → [1]
    applySelection(this.elements.weightPlatesBtns, 'plates', 'weightPlates');

    // insert: yes    → [0]  |  no     → [1]
    applySelection(this.elements.insertBtns,       'insert', 'insert');
  }

  
  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Pack size buttons
    this.elements.packSizeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleOptionChange(e, 'packSize'));
    });
    
    // Weight plates buttons
    this.elements.weightPlatesBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleOptionChange(e, 'weightPlates'));
    });
    
    // Insert buttons
    this.elements.insertBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleOptionChange(e, 'insert'));
    });
    
    // Quantity controls
    this.elements.qtyMinus.addEventListener('click', () => this.decreaseQuantity());
    this.elements.qtyPlus.addEventListener('click', () => this.increaseQuantity());
    this.elements.qtyInput.addEventListener('change', (e) => this.handleQuantityChange(e));
    
    // Thumbnail clicks
    this.bindThumbnailEvents();
    
    // Add to cart
    this.elements.addToCartBtn.addEventListener('click', () => this.addToCart());

    // More Payment Option
    this.elements.morePaymentBtn.addEventListener('click', () => this.addToCartMorePayment());   
  }

  /**   
  * Bind thumbnail click events   
  */  
  bindThumbnailEvents() {    
    this.elements.thumbItems.forEach(thumb => {      
      thumb.removeEventListener('click', this.thumbClickHandler);      
      this.thumbClickHandler = () => this.handleThumbClick(thumb);      
      thumb.addEventListener('click', this.thumbClickHandler);    
    });  
  }  
  
  /**
   * Handle option button clicks
   */
  handleOptionChange(event, stateKey) {
    const button = event.currentTarget;
    const value = button.dataset.value;
    
    // Update state
    this.state[stateKey] = value;
    
    // Update button active states
    const group = button.closest('.option-group');
    group.querySelectorAll('.option-btn').forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    
    // Update display
    this.updateDisplay();
  }
  
  /**
   * Decrease quantity
   */
  decreaseQuantity() {
    const currentQty = parseInt(this.elements.qtyInput.value);
    if (currentQty > 1) {
      this.elements.qtyInput.value = currentQty - 1;
      this.state.quantity = currentQty - 1;
      this.updatePrice();
    }
  }
  
  /**
   * Increase quantity
   */
  increaseQuantity() {
    const currentQty = parseInt(this.elements.qtyInput.value);
    const maxQty = parseInt(this.elements.qtyInput.max) || 10;
    if (currentQty < maxQty) {
      this.elements.qtyInput.value = currentQty + 1;
      this.state.quantity = currentQty + 1;
      this.updatePrice();
    }
  }
  
  /**
   * Handle manual quantity input
   */
  handleQuantityChange(event) {
    let qty = parseInt(event.target.value);
    const min = parseInt(event.target.min) || 1;
    const max = parseInt(event.target.max) || 10;
    
    if (isNaN(qty) || qty < min) qty = min;
    if (qty > max) qty = max;
    
    event.target.value = qty;
    this.state.quantity = qty;
    this.updatePrice();
  }
  
  /**
   * Handle thumbnail click
   */
  handleThumbClick(thumb) {
    const imageUrl = thumb.dataset.imageSrc;
    const imageAlt = thumb.dataset.imageAlt;
    
    // Update main image
    this.elements.mainImage.style.opacity = '0';
    setTimeout(() => {
      this.elements.mainImage.src = imageUrl;
      this.elements.mainImage.alt = imageAlt;
      this.elements.mainImage.style.opacity = '1';
    }, 150);
    
    // Update active thumbnail
    this.elements.thumbItems.forEach(item => item.classList.remove('active'));
    thumb.classList.add('active');
  }
  
  /**
   * Update all displays (price, image)
   */
  updateDisplay() {
    this.updatePrice();
    this.updateImage();
  }
  
    /**
     * Calculate and update price display
     */
    updatePrice() {
        if (!this.state.packSize || !this.state.weightPlates || this.state.insert === null) {
            return;
        }

        let totalPrice = 0;
        const breakdown = [];
        const packMultiplier = this.state.packSize === 'pair' ? 1 : 1;
        const quantity = this.state.quantity || 1; // Get selected quantity
        const finalMultiplier = packMultiplier * quantity;
        var comparePrice = 0;
        var savePrice;        

        // Determine price based on selections
        if (this.state.packSize === 'single') {
            if (this.state.weightPlates === 'yes') {
              if (this.state.insert === 'no') {
                const basePrice = this.config.baseProductPrice; // SKU 1, if Qty is 1
                totalPrice += basePrice;
                comparePrice = this.config.baseProductComparePrice;
                savePrice = this.config.baseProductSavePrice;                  
                breakdown.push({
                    name: `KettleBull Pro`,
                    price: basePrice,
                    quantity: finalMultiplier
                });                
              }else{
                const kbProInsertProductPrice = this.config.kbProInsertProductPrice; // SKU 5
                totalPrice += kbProInsertProductPrice;
                comparePrice = this.config.kbProInsertProductComparePrice;
                savePrice = this.config.kbProInsertProductSavePrice;                
                breakdown.push({
                    name: `KettleBull Pro (+30lb Insert)`,
                    price: kbProInsertProductPrice,
                    quantity: finalMultiplier
                });
              }  
            } else {
                if (this.state.insert === 'yes') {
                    const kbProAssemblyInsertProductPrice = this.config.kbProAssemblyInsertProductPrice; // SKU 7
                    totalPrice += kbProAssemblyInsertProductPrice;
                    comparePrice = this.config.kbProAssemblyInsertProductComparePrice;
                    savePrice = this.config.kbProAssemblyInsertProductSavePrice;                    
                    breakdown.push({
                        name: `KettleBull Pro Handle Assembly (+30lb Insert)`,
                        price: kbProAssemblyInsertProductPrice,
                        quantity: finalMultiplier
                    });
                }else{
                  const assemblyPrice = this.config.handleAssemblyPrice; // SKU 2
                  totalPrice += assemblyPrice;
                  comparePrice = this.config.handleAssemblyProductComparePrice;
                  savePrice = this.config.handleAssemblyProductSavePrice;
                  breakdown.push({
                      name: `Handle Assembly`,
                      price: assemblyPrice,
                      quantity: finalMultiplier
                  });
                }              
            }
        } else if (this.state.packSize === 'pair') {
            if (this.state.weightPlates === 'yes') {
              if (this.state.insert === 'no') {
                const kbProPairProductPrice = this.config.kbProPairProductPrice; // SKU 4
                totalPrice += kbProPairProductPrice;
                comparePrice = this.config.kbProPairProductComparePrice;
                savePrice = this.config.kbProPairProductSavePrice;                
                breakdown.push({
                    name: `KettleBull Pro (Pair)`,
                    price: kbProPairProductPrice,
                    quantity: finalMultiplier
                });                
              }else{
                const kbProInsertPairProductPrice = this.config.kbProInsertPairProductPrice; // SKU 6
                totalPrice += kbProInsertPairProductPrice;
                comparePrice = this.config.kbProInsertPairProductComparePrice;
                savePrice = this.config.kbProInsertPairProductSavePrice;                
                breakdown.push({
                    name: `KettleBull Pro +30lb Insert (Pair)`,
                    price: kbProInsertPairProductPrice,
                    quantity: finalMultiplier
                });
              }
            } else {
                if (this.state.insert === 'no') {
                  const kbProAssemblyPairProductPrice = this.config.kbProAssemblyPairProductPrice; // SKU 9
                  totalPrice += kbProAssemblyPairProductPrice;
                  comparePrice = this.config.kbProAssemblyPairProductComparePrice;
                  savePrice = this.config.kbProAssemblyPairProductSavePrice;                  
                  breakdown.push({
                      name: `KettleBull Pro Handle Assembly (Pair)`,
                      price: kbProAssemblyPairProductPrice,
                      quantity: finalMultiplier
                  });
                }else{
                  const kbProAssemblyInsertPairProductPrice = this.config.kbProAssemblyInsertPairProductPrice; // SKU 8
                  totalPrice += kbProAssemblyInsertPairProductPrice;
                  comparePrice = this.config.kbProAssemblyInsertPairProductComparePrice;
                  savePrice = this.config.kbProAssemblyInsertPairProductSavePrice;                  
                  breakdown.push({
                      name: `KettleBull Pro Handle Assembly +30lb Insert (Pair)`,
                      price: kbProAssemblyInsertPairProductPrice,
                      quantity: finalMultiplier
                  });
                }  
            }
        }

        // Update price display
        this.elements.priceDisplay.textContent = this.formatPrice(totalPrice);
        this.elements.priceDisplay.dataset.price = totalPrice;
        //console.log("SavePrice =>", savePrice);
        if(savePrice == "null" || savePrice == '' || savePrice == undefined){
          //console.log("Save Price is emplty")
          this.elements.comparePriceDisplay.classList.add('hidden');
          this.elements.savePriceDisplay.classList.add('hidden');
        }else{
          this.elements.comparePriceDisplay.classList.remove('hidden');
          this.elements.savePriceDisplay.classList.remove('hidden');    
        }    
        
        this.elements.comparePriceDisplay.textContent = this.formatPrice(comparePrice);  
        this.elements.comparePriceDisplay.dataset.compareprice = comparePrice; 
        
        this.elements.savePriceDisplay.textContent = savePrice;  
        this.elements.savePriceDisplay.dataset.saveprice = savePrice;         

        // Update price breakdown if element exists
        if (this.elements.priceBreakdown) {
            this.elements.priceBreakdown.innerHTML = breakdown.map(item => `
                <div class="price-breakdown-item">
                    <span>${item.name}</span>                
                    <span class="hidden">${this.formatPrice(item.price)}</span>
                </div>
            `).join('');
        }
    }


  
  /**
   * Update main image based on current selection
   */
  updateImage() {
    if (!this.state.packSize || !this.state.weightPlates || this.state.insert === null) {
      return;
    }
    
    // Find matching combination
    const combination = this.config.combinations.find(combo => 
      combo.packSize === this.state.packSize &&
      combo.weightPlates === this.state.weightPlates &&
      combo.insert === this.state.insert
    );
    
    if (combination && combination.image) {
      // Mark that we're now viewing the selected combination (not base product)
      this.state.isBaseProductView = false;
      // Update main image with fade effect
      this.elements.mainImage.style.opacity = '0';
      setTimeout(() => {
        this.elements.mainImage.src = combination.image;
        this.elements.mainImage.alt = `${combination.packSize} - ${combination.weightPlates} - ${combination.insert}`;
        this.elements.mainImage.style.opacity = '1';
      }, 150);

      // Update thumbnails to show base product images      
      // The main image is now the selected combination, thumbnails are base product      
      this.updateThumbnailsGallery(this.config.baseProductImages, 'base');      
      
      // // Update active thumbnail
      // const combinationKey = `${combination.packSize}-${combination.weightPlates}-${combination.insert}`;
      // this.elements.thumbItems.forEach(thumb => {
      //   if (thumb.dataset.combination === combinationKey) {
      //     thumb.classList.add('active');
      //   } else {
      //     thumb.classList.remove('active');
      //   }
      // });
    }
  }
  
  
    /**
     * Add products to cart with correct SKUs based on selections
     */
    async addToCart() {
        if (!this.state.packSize || !this.state.weightPlates || this.state.insert === null) {
            alert('Please select all options before adding to cart.');
            return;
        }

        // Show loading state
        this.elements.addToCartBtn.disabled = true;
        this.elements.loadingSpinner?.classList.remove('hidden');

        try {
            const items = this.buildCartItems();

            // Add to cart via Shopify Ajax API
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ items }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || 'Failed to add to cart');
            }

            const result = await response.json();
            console.log('Added to cart:', result);            
            
            // Success feedback
            this.showSuccessMessage();

            this.elements.addToCartBtn.disabled = false;
            this.elements.loadingSpinner?.classList.add('hidden');
            // window.location.href = '/checkout';
            monster_refresh(open);

            // Trigger cart update event
            //document.dispatchEvent(new CustomEvent('cart:updated'));

        } catch (error) {
            console.error('Add to cart error:', error);
            alert(`Error adding to cart: ${error.message}`);
        } finally {
            // Hide loading state
            this.elements.addToCartBtn.disabled = false;
            this.elements.loadingSpinner?.classList.add('hidden');
        }
    }
    
    // Add Products to the cart and go straight to the checkout for more payment options

    async addToCartMorePayment () {
      if (!this.state.packSize || !this.state.weightPlates || this.state.insert === null) {
          alert('Please select all options before adding to cart.');
          return;
      }

      // Show loading state
      this.elements.addToCartBtn.disabled = true;
      this.elements.loadingSpinner?.classList.remove('hidden');

      try {
          const items = this.buildCartItems();

          // Add to cart via Shopify Ajax API
          const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({ items }),
          });

          if (!response.ok) {
              const error = await response.json();
              throw new Error(error.description || 'Failed to add to cart');
          }

          const result = await response.json();
          console.log('Added to cart:', result);            
          
          // Success feedback
          this.showSuccessMessage();

          this.elements.addToCartBtn.disabled = false;
          this.elements.loadingSpinner?.classList.add('hidden');
          window.location.href = '/checkout';
          //monster_refresh(open);

          // Trigger cart update event
          //document.dispatchEvent(new CustomEvent('cart:updated'));

      } catch (error) {
          console.error('Add to cart error:', error);
          alert(`Error adding to cart: ${error.message}`);
      } finally {
          // Hide loading state
          this.elements.addToCartBtn.disabled = false;
          this.elements.loadingSpinner?.classList.add('hidden');
      }
    };    

    /**
     * Build cart items array based on current selection
     */
    buildCartItems() {
        const items = [];
        const packMultiplier = this.state.packSize === 'pair' ? 1 : 1;
        const quantity = this.state.quantity || 1; // Get selected quantity

        // Calculate final quantity based on pack multiplier and selected quantity    
        const finalQuantity = packMultiplier * quantity;        

        // Determine which SKUs to add based on selections
        if (this.state.packSize === 'single') {
            if (this.state.weightPlates === 'yes') {
              if (this.state.insert === 'no') {
                // Option 1B
                items.push({
                    id: this.config.baseProductId, // SKU 1
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });                
              }else{
                // Option 2
                items.push({
                    id: this.config.kbProInsertProductId, // SKU 5
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });
              }                  
            }else {
              if (this.state.insert === 'yes') {
                // Option 4  
                items.push({
                    id: this.config.kbProAssemblyInsertProductId, // SKU 7
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });
              }else{
                // Option 3 - No weight plates
                items.push({
                    id: this.config.handleAssemblyId, // SKU 2
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });
              }  
            }
        }else if (this.state.packSize === 'pair') {
            if (this.state.weightPlates === 'yes') {
              if (this.state.insert === 'no') {
                // Option 5  
                items.push({
                    id: this.config.kbProPairProductId, // SKU 4
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });                
              }else{
                // Option 6  
                items.push({
                    id: this.config.kbProInsertPairProductId, // SKU 6
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                }); 
              }  
            } else {
              if (this.state.insert === 'no') {
                // Option 7  
                items.push({
                    id: this.config.kbProAssemblyPairProductId, // SKU 9
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });                
              }else{
                // Option 8  
                items.push({
                    id: this.config.kbProAssemblyInsertPairProductId, // SKU 8
                    quantity: finalQuantity,
                    properties: this.buildLineItemProperties()
                });  
              }  
            }
        }

        return items;
    }

/** * Build line item properties for cart items * This method creates custom properties that can be passed with each cart item */
buildLineItemProperties() {    
  const properties = {};    
  // Add pack size property    
  if (this.state.packSize) {        
    properties['Pack Size'] = this.state.packSize;    
  }    
  // Add weight plates property    
  if (this.state.weightPlates) {        
    properties['Weight Plates'] = this.state.weightPlates === 'yes' ? 'Yes' : 'No';    
  }    
  // Add insert property    
  if (this.state.insert !== null) {        
    properties['Additional Insert'] = this.state.insert === 'yes' ? '+30 LB Insert' : 'No Insert';    
  }   
  return properties;
}    


  
  /**
   * Show success message
   */
  showSuccessMessage() {
    const originalText = this.elements.addToCartBtn.querySelector('.btn-text').textContent;
    this.elements.addToCartBtn.querySelector('.btn-text').textContent = '✓ ADDED TO CART';
    this.elements.addToCartBtn.style.background = '#4CAF50';
    
    setTimeout(() => {
      this.elements.addToCartBtn.querySelector('.btn-text').textContent = originalText;
      this.elements.addToCartBtn.style.background = '';
    }, 2000);
  }
  
  /**
   * Format price in dollars
   */
  formatPrice(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const bundleSections = document.querySelectorAll('[data-section-type="custom-product-bundle"]');
  bundleSections.forEach(section => {
    new CustomProductBundle(section);
  });
});

// Re-initialize on Shopify section events (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="custom-product-bundle"]');
    if (section) {
      new CustomProductBundle(section);
    }
  });
}
